import { useMutation } from "@apollo/client";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { useSnackbar } from "notistack";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProfileReviewFormFields, profileReviewSchema } from "../formSchema";
import { ApproveWorkerProfile, DeclineWorkerProfile, WorkerMandatoryAttributes } from "../queries.graphql";
import type {
  ApproveWorkerProfileMutation,
  ApproveWorkerProfileMutationVariables,
  DeclineWorkerProfileMutation,
  DeclineWorkerProfileMutationVariables,
  WorkerMandatoryAttributesQuery,
} from "../queries.types";
import { InferType } from "yup";
import { SubmitHandler } from "react-hook-form";
import { isAttribute, isSystem } from "@sinch/components/application/ProfileReviewDialog/tools";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import { useSettings } from "@sinch/core/providers/AppSettings";
import {
  parseAttributeValue,
  serializeEditedValue,
} from "@sinch/components/application/Worker/Attributes/tools";
import { equals } from "ramda";

// Outside callers (StaffDetailPanel banner, NewStaffMemberForm banner) cache
// `profileApprovalStatus` / `hasPendingChanges` / `declineMessage` on the worker.
// Refetch their queries so the banner state matches the new server truth without
// a full page reload. `awaitRefetchQueries` keeps the dialog's loading spinner
// visible until the refetched data lands.
const mutationOptions = {
  errorPolicy: "all" as const,
  awaitRefetchQueries: true,
  refetchQueries: ["StaffDetail", "GetWorker"],
};

interface UseProfileReviewProps {
  workerId: string;
  onSuccess: () => void;
}

export const useProfileReview = ({ workerId, onSuccess }: UseProfileReviewProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { timeZone } = useSettings();

  const { data: mandatoryData } = useSuspenseQueryHandler<WorkerMandatoryAttributesQuery>(WorkerMandatoryAttributes, {
    workerId,
  });

  const parsedValuesAttributes = useMemo(
    () =>
      mandatoryData?.workerMandatoryAttribute.map((item) => ({
        ...item,
        value: parseAttributeValue(
          isAttribute(item) ? item.attribute.dataType : item.dataType,
          item.value,
          item.files ?? [],
          timeZone
        ),
      })) ?? [],
    [mandatoryData, timeZone]
  );

  const defaultValues = useMemo<InferType<typeof profileReviewSchema>>(
    () => ({
      attributes: parsedValuesAttributes?.map((item) => ({
        attributeId: isAttribute(item) ? item.attribute.id : null,
        type: isAttribute(item) ? "attribute" : "system",
        value: item.value,
        fieldName: isSystem(item) ? item.field : null,
        expirationDate: isAttribute(item) ? item.expirationDate : null,
      })),
    }),
    [parsedValuesAttributes]
  );

  const form = useMuiForm<ProfileReviewFormFields>({
    schema: profileReviewSchema,
    formOptions: {
      defaultValues,
      mode: "onChange",
    },
  });

  const [approveProfile, { loading: approveLoading }] = useMutation<
    ApproveWorkerProfileMutation,
    ApproveWorkerProfileMutationVariables
  >(ApproveWorkerProfile, mutationOptions);

  const [declineProfile, { loading: declineLoading }] = useMutation<
    DeclineWorkerProfileMutation,
    DeclineWorkerProfileMutationVariables
  >(DeclineWorkerProfile, mutationOptions);

  const handleApprove = useCallback<SubmitHandler<ProfileReviewFormFields>>(
    async (data): Promise<boolean> => {
      const expirationDates: ApproveWorkerProfileMutationVariables["expirationDates"] = data.attributes
        .filter(({ type }) => type === "attribute")
        .map((item) => ({
          attributeId: item.attributeId!,
          expirationDate: item.expirationDate ?? null,
        }));

      // Only forward rows the admin actually edited — parsed-but-unchanged values
      // would otherwise be re-serialised and written back over the worker's submission.
      // For each edited row we re-serialise the UI-shaped value (Date object, file
      // list of {id,url,name}) back into the scalar shape the backend resolver expects.
      const editedValues: ApproveWorkerProfileMutationVariables["editedValues"] = data.attributes.flatMap(
        (item, ix) => {
          const source = parsedValuesAttributes[ix];
          if (source === undefined) {
            return [];
          }
          if (equals(source.value, item.value)) {
            return [];
          }
          const dataType = isAttribute(source) ? source.attribute.dataType : source.dataType;
          return [
            {
              attributeId: item.attributeId ?? null,
              editedValue: serializeEditedValue(dataType, item.value),
              fieldName: item.fieldName ?? null,
            },
          ];
        }
      );

      try {
        const response = await approveProfile({
          variables: { workerId, expirationDates, editedValues },
        });
        if (response.data?.approveWorkerProfile.result) {
          enqueueSnackbar(t("ProfileReview.profileApproved"), { variant: "success" });
          // Note: caller decides whether to call onSuccess (closes dialog) or auto-advance
          // to the pending changes step. See ProfileReviewDialog.handleConfirmApprove.
          return true;
        }
        const messages = response.data?.approveWorkerProfile.messages;
        enqueueSnackbar(messages?.[0]?.message ?? t("ProfileReview.error"), { variant: "error" });
        return false;
      } catch {
        enqueueSnackbar(t("ProfileReview.error"), { variant: "error" });
        return false;
      }
    },
    [approveProfile, workerId, enqueueSnackbar, t, parsedValuesAttributes]
  );

  const handleDecline = useCallback(
    async (message: string) => {
      try {
        const response = await declineProfile({ variables: { workerId, message } });
        if (response.data?.declineWorkerProfile.result) {
          enqueueSnackbar(t("ProfileReview.profileDeclined"), { variant: "success" });
          onSuccess();
        } else {
          const messages = response.data?.declineWorkerProfile.messages;
          enqueueSnackbar(messages?.[0]?.message ?? t("ProfileReview.error"), { variant: "error" });
        }
      } catch {
        enqueueSnackbar(t("ProfileReview.error"), { variant: "error" });
      }
    },
    [declineProfile, workerId, enqueueSnackbar, t, onSuccess]
  );

  return {
    form,
    handleApprove,
    handleDecline,
    loading: approveLoading || declineLoading,
    attributes: parsedValuesAttributes,
  };
};
